import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

// 表格里的一格：点一下就地编辑，失焦或 ⌘↵ 保存，Esc 放弃。
// multiline 时回车换行；单行时回车直接保存。

export function Editable({
  value,
  onSave,
  canEdit,
  placeholder,
  multiline = false,
  maxLength,
  className,
  clearOnEdit,
}: {
  value: string;
  onSave: (v: string) => void;
  canEdit: boolean;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
  className?: string;
  /** 内容是这句自动生成的占位话时，点开编辑直接清空 */
  clearOnEdit?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!editing) setText(value);
  }, [value, editing]);
  useEffect(() => {
    const el = ref.current;
    if (!editing || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, editing]);

  const open = () => {
    if (clearOnEdit && value.trim() === clearOnEdit) setText('');
    setEditing(true);
  };
  const save = () => {
    setEditing(false);
    if (clearOnEdit && !text.trim() && value.trim() === clearOnEdit) return;
    if (text.trim() !== value.trim()) onSave(text.trim());
  };

  if (editing)
    return (
      <textarea
        ref={ref}
        className={clsx('pg-edit', className)}
        value={text}
        rows={1}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
          }
          if (e.key === 'Escape') {
            e.stopPropagation();
            setText(value);
            setEditing(false);
          }
        }}
      />
    );
  return (
    <span
      className={clsx('pg-text', canEdit && 'editable', !value.trim() && 'empty', className)}
      onClick={() => canEdit && open()}
      onKeyDown={(e) => canEdit && e.key === 'Enter' && open()}
      tabIndex={canEdit ? 0 : undefined}
      role={canEdit ? 'button' : undefined}
      title={canEdit ? '点击修改' : undefined}
    >
      {value.trim() || (canEdit ? placeholder : '—')}
    </span>
  );
}
